// angular
import { Component, effect, inject, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
// components
import { ButtonComponent } from '@core/components/button/button.component';
import { InputComponent } from '@core/components/input/input.component';
import { PhoneNumberComponent } from '@core/components/phone-number/phone-number.component';
import { LoginDialogComponent } from '../login-dialog/login-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
// services
import { StorageService } from '@shared/services/storage.service';
import { InitService } from '@shared/services/init.service';
import { AuthService } from '@app/shared/services/auth.service';
import { UserService } from '@app/shared/services/user.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
// validators & directives
import { ConfirmPasswordValidator } from '@shared/validators/confirm-password.validator';
import { OnlyNumberDirective } from '@shared/directives/only-number.directive';
// types & interfaces
import { II18n } from '@shared/interfaces/i18n.interface';
import { IResendOtp, IVerified } from '@app/shared/interfaces/signup.interface';
// enums & utils
import { I18n } from '@shared/enums/i18n.enum';
import { DialogRef } from '@app/core/shared/utils/dialog/dialog-ref';
import { DIALOG_DATA } from '@app/core/shared/utils/dialog/dialog-tokens';
import {IPhoneNumber} from "@core/shared/interfaces/phone-number.interface";
import {LoadingService} from "@shared/services/loading.service";
import {MatomoTrackClickDirective} from "ngx-matomo-client";

@Component({
  selector: 'pwj-signup-dialog',
  standalone: true,
  imports: [
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    TranslateModule,
    PhoneNumberComponent,
    OnlyNumberDirective,
    MatomoTrackClickDirective,
  ],
  templateUrl: './signup-dialog.component.html',
  styleUrl: './signup-dialog.component.scss',
})
export class SignupDialogComponent implements OnInit{
  public signupForm: FormGroup;
  public signupOtpForm: FormGroup;
  public languages: II18n = I18n;
  public currentLang: keyof II18n;
  public otpTimer: number = 120;
  public showResendButton: boolean = true;
  public isVerifyOtpBtnDisable: boolean = false;

  private _timerInterval: any;
  private _userInfo!: IResendOtp;

  private readonly fb = inject(FormBuilder);
  private readonly _initService = inject(InitService);
  public readonly authService = inject(AuthService);
  private readonly _storageService = inject(StorageService);
  private readonly _translateService = inject(TranslateService);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _dialogService = inject(DialogService);
  private readonly _loadingService = inject(LoadingService);
  public readonly data = inject(DIALOG_DATA);

  constructor() {
    this.currentLang = this._translateService.currentLang as keyof II18n;
    this.signupForm = this.fb.group(
      {
        mobile: ['', Validators.required],
        password: ['', [Validators.required, Validators.minLength(8)]],
        password_confirmation: ['', Validators.required],
      },
      { validators: ConfirmPasswordValidator }
    );
    this.signupOtpForm = this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      otp: ['', Validators.required],
    });

    effect(() => {
      const userPhone = this.authService.userSignUpState().mobile;

      if (userPhone) {
        this._userInfo = {
          ...userPhone,
        };
      }
    });
  }

  public close(isCloseByUser: boolean = false) {
    if (isCloseByUser && this.authService.userSignUpState().state === 1) {
      const dialogRef = this._dialogService.open(ConfirmationDialogComponent, {
        width: '26em',
        height: '10em',
        data: {
          title: 'auth.closeDialogWarning',
          description: 'auth.closeOtpDialogMessage',
        },
      });

      dialogRef.afterClosed().subscribe(res => {
        if (res) {
          this._dialogRef.close();
        }
      });
    } else {
      this._dialogRef.close();
    }
  }

  public openLoginDialog() {
    this.close();
    this._dialogService.open(LoginDialogComponent, {
      width: 'none',
      height: 'none',
    });
  }

  ngOnInit() {
    if (this.authService.userSignUpState()?.mobile_verified === undefined){
      this.startOtpTimer();
    }
  }

  changeLang(lang: string) {
    this._translateService.use(lang);
    this.currentLang = lang as keyof II18n;
  }

  submit() {
    this.signupForm.markAllAsTouched();
    if (this.signupForm.invalid || !this.signupForm.dirty) return;
    this._loadingService.setLoading(true);
    let params = this.signupForm.value;
    if (
      this._storageService.getChatId() &&
      this._storageService.getMemberId()
    ) {
      params = {
        ...params,
        chatId: this._storageService.getChatId(),
        memberId: this._storageService.getMemberId(),
      };
    }
    this.authService.signUp(params).subscribe({
      next: (res) => {
        if (res) {
          this._storageService.setToken(res.access_token);
          this._storageService.setRefreshToken(res.refresh_token);
          this.authService.updateUserSignUpState({
            state: 1,
            mobile: params.mobile,
            hash: res.hash,
          });

          this._userInfo = {
            prefix: params.mobile.prefix,
            mobile_number: params.mobile.mobile_number
          };

          this.startOtpTimer();
          this._loadingService.setLoading(false)
        }
      },
      error: () => {
        this._loadingService.setLoading(false)
      },
      complete: ()=> this._loadingService.setLoading(false)
    });
  }

  submitOtp() {
    this.signupOtpForm.markAllAsTouched();
    if (this.signupOtpForm.invalid || !this.signupOtpForm.dirty) return;
    this._loadingService.setLoading(true)
    const params: IVerified = {
      ...this.signupOtpForm.value,
      otp: Number(this.signupOtpForm.controls['otp'].value),
      hash: this.authService.userSignUpState().hash,
      mobile: this.authService.userSignUpState().mobile || this._userInfo,
    };
    this.isVerifyOtpBtnDisable = true;

    this.authService.verifyOtp(params).subscribe({
      next: (res) => {
        if (res && res.success) {
          this._initService.init();
          this._loadingService.setLoading(false)
          this._dialogRef.close();
        }
      },
      error: err => {
        this.isVerifyOtpBtnDisable = false;
        this._loadingService.setLoading(false)
      },
      complete: ()=> this._loadingService.setLoading(false)
    });
  }

  startOtpTimer() {
    this.otpTimer = 120;
    this.showResendButton = false;

    this._timerInterval = setInterval(() => {
      if (this.otpTimer > 0) {
        this.otpTimer--;
      } else {
        this.showResendButton = true;
        clearInterval(this._timerInterval);
      }
    }, 1000);
  }

  get formattedTimer() {
    const minutes = Math.floor(this.otpTimer / 60);
    const seconds = this.otpTimer % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  resendOtp() {
    if (this._userInfo) {
      this.startOtpTimer();

      this.authService.resendOtp({...this._userInfo}).subscribe({
        next: res => {
          this.authService.updateUserSignUpState({ hash: res.hash });
        },
        error: error => {},
      });
    }
  }

  ngOnDestroy() {
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
    }
  }
}
